import { useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle, ShieldQuestion } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useSetSecurityQuestion } from '@/api/hooks/useAuthExtra';
import { useSession } from '@/auth/session';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';

/**
 * Sets the security question used by the "forgot password" question-based
 * reset path. Personal, like the password card next to it — every user sets
 * their own, regardless of role.
 */
export function SecurityQuestionCard() {
  const { user, refresh } = useSession();
  const setQuestion = useSetSecurityQuestion();

  const [question, setQuestionText] = useState('');
  const [answer, setAnswer] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      await setQuestion.mutateAsync({ question, answer, currentPassword });
      toast({ title: 'Security question saved', variant: 'success' });
      setQuestionText('');
      setAnswer('');
      setCurrentPassword('');
      void refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldQuestion className="h-4 w-4" /> Security question
        </CardTitle>
      </CardHeader>
      <CardContent>
        {user?.hasSecurityQuestion && (
          <p className="mb-3 text-xs text-muted-foreground">
            Currently set: "{user.securityQuestion}". Saving below replaces it.
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="sq-question">Question</Label>
            <Input
              id="sq-question"
              value={question}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="e.g. What was your first pet's name?"
              required
              minLength={5}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="sq-answer">Answer</Label>
              <Input id="sq-answer" value={answer} onChange={(e) => setAnswer(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sq-password">Current password</Label>
              <Input
                id="sq-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}

          <Button type="submit" disabled={setQuestion.isPending}>
            {setQuestion.isPending && <LoaderCircle className="animate-spin" />}
            Save security question
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
